import {
  useCallback,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import messages from "messages";
// components
import PaneLayout from "../pane-layout";
// hooks
import { useSessionTypes } from "contexts/SessionTypesContext";
import useToggleModal from "hooks/useToggleModal";
// utils
import SessionTypesUI from "components/session-types-ui/SessionTypesUI";
import SessionTypeEditor from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/session-type-editor-context/components/session-type-editor/SessionTypeEditor";
import ConfirmModal from "components/confirm-modal";
// services
import postSessionTypes from "services/scheduler/postAppointmentTypes";
// constants
import { DEFAULT_SESSION_TYPES } from "constants/defaultSessionTypes";
import { DEFAULT_SESSION_TYPE } from "constants/defaultSessionType";
// styles
import styles from "./SessionTypesPane.module.scss";
// types
import type { PaneProps, PaneRefProps } from "../../MainSetup";

const ForwardedSessionTypesPane = forwardRef<PaneRefProps, PaneProps>(
  function SessionTypesPane({ onShowModal, amplitudePageName, ...rest }, ref) {
    const { sessionTypes, setSessionTypes: onDone } = useSessionTypes();
    const {
      show: showSessionTypesEditor,
      open: openSessionTypesEditor,
      close: closeSessionTypesEditor,
    } = useToggleModal(false);
    const {
      show: showCountAlert,
      open: openCountAlert,
      close: closeCountAlert,
    } = useToggleModal(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    const handleCloseSessionTypesEditor = useCallback(() => {
      setActiveIndex(-1);
      closeSessionTypesEditor();
    }, [closeSessionTypesEditor]);

    useEffect(
      () => onShowModal(showSessionTypesEditor),
      [showSessionTypesEditor, onShowModal],
    );

    const [localSessionTypes, setLocalSessionTypes] = useState<SessionType[]>(
      sessionTypes.length === 0 ? DEFAULT_SESSION_TYPES : sessionTypes,
    );

    const handleApply = useCallback(async () => {
      await postSessionTypes(localSessionTypes);
      if (onDone) {
        onDone(localSessionTypes);
      }
    }, [onDone, localSessionTypes]);

    useImperativeHandle(ref, () => ({
      handleApply,
    }));

    return (
      <PaneLayout
        Title={messages.createAFirstSessionType}
        Subtitle={messages.theseAreTheTypesOfSessions}
        {...rest}
      >
        <div className={styles["session-types-pane__list"]}>
          <SessionTypesUI
            sessionTypes={localSessionTypes}
            onApply={(changedElementValue, changedElementIndex) =>
              setLocalSessionTypes((s) =>
                s.filter((element, index) =>
                  index === changedElementIndex ? changedElementValue : element,
                ),
              )
            }
            onEdit={(_, changedElementIndex) => {
              setActiveIndex(changedElementIndex);
              openSessionTypesEditor();
            }}
            onDelete={(v) => {
              // if it is the last one, show count alert
              if (localSessionTypes.length < 2) {
                openCountAlert();
                return;
              }

              setLocalSessionTypes((s) => s.filter((el) => el !== v));
            }}
            onAdd={openSessionTypesEditor}
            setSessionTypes={setLocalSessionTypes}
            variant="compact"
          />
        </div>

        {/* session type editor modal */}
        <SessionTypeEditor
          defaultValue={localSessionTypes[activeIndex] || DEFAULT_SESSION_TYPE}
          onApply={(v) =>
            setLocalSessionTypes(
              activeIndex > -1
                ? [
                    ...localSessionTypes.slice(0, activeIndex),
                    v,
                    ...localSessionTypes.slice(activeIndex + 1),
                  ]
                : [...localSessionTypes, v],
            )
          }
          show={showSessionTypesEditor}
          onHide={handleCloseSessionTypesEditor}
          backdrop={false}
          // check label duplicated
          getIsLabelInvalid={(arg: string | undefined) =>
            !arg ? false : localSessionTypes.some((el) => el.label === arg)
          }
          amplitudePageName={amplitudePageName}
        />
        {/* confirm not to proceed with less than one session type */}
        <ConfirmModal
          labelTitle={messages.createAtLeast1Session}
          labelConfirm={messages.ok}
          hideCancel={true}
          onCancel={closeCountAlert}
          onConfirm={closeCountAlert}
          show={showCountAlert}
        >
          {messages.youNeedToCreateAtLeast1SessionType}
        </ConfirmModal>
      </PaneLayout>
    );
  },
);

export default ForwardedSessionTypesPane;
